import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const useCountUp = (end: number, duration: number = 2000) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;
    let startTime: number | null = null;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [hasStarted, end, duration]);

  return { count, ref };
};

const AboutPage = () => {
  const [activeTab, setActiveTab] = useState(0);
  const stat1 = useCountUp(8, 1800);
  const stat2 = useCountUp(60000, 2000);
  const stat3 = useCountUp(100, 1800);
  const stat4 = useCountUp(98, 2200);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    const schemaId = 'schema-about';
    if (!document.getElementById(schemaId)) {
      const script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "AboutPage",
        "name": "Branch(브란치) 회사소개 | 글로벌 역량 교육 전문 기업",
        "description": "Branch(브란치) 대표 김수호의 인사말, 비전, 사업 목표를 소개합니다. 국제교류와 진로교육을 통해 미래 인재의 글로벌 역량을 키웁니다.",
        "url": siteUrl + "/about",
        "inLanguage": "ko-KR",
        "isPartOf": {
          "@type": "WebSite",
          "name": "Branch(브란치)",
          "url": siteUrl
        },
        "breadcrumb": {
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "홈", "item": siteUrl + "/" },
            { "@type": "ListItem", "position": 2, "name": "회사소개", "item": siteUrl + "/about" }
          ]
        },
        "mainEntity": {
          "@type": "Person",
          "name": "김수호",
          "jobTitle": "브란치 대표",
          "worksFor": {
            "@type": "EducationalOrganization",
            "name": "Branch(브란치)"
          }
        }
      });
      document.head.appendChild(script);
    }
    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  const missionItems = [
    {
      icon: 'ri-global-line',
      title: '글로벌 영역 강화',
      desc:
        '국제교육 프로그램을 통해 학생들이 다양한 문화를 경험하고, 글로벌 시대에 필요한 소통 능력과 적응력을 키웁니다.'
    },
    {
      icon: 'ri-compass-3-line',
      title: '진로 탐색 지원',
      desc:
        '다양한 직업 체험과 대학교 탐방을 통해 학생들이 자신의 적성과 흥미를 발견하고, 구체적인 진로 목표를 설정할 수 있도록 돕습니다.'
    },
    {
      icon: 'ri-lightbulb-line',
      title: '미래 인재 양성',
      desc:
        'AI 시대에 필요한 창의적 사고와 문제 해결 능력을 갖춘 미래 인재를 양성합니다.'
    },
    {
      icon: 'ri-team-line',
      title: '소통 능력 향상',
      desc:
        '다양한 사람들과의 교육을 통해 소통 능력과 협업 능력이 자연스럽게 향상됩니다.'
    }
  ];

  const careerItems = [
    '교육부 관련 진로교육 프로그램 기획 및 운영',
    '한·일 국제교육 사업 확대',
    '청소년 진로교육 업체 성장 8년 이상'
  ];

  const activityItems = [
    '브란치 대표',
    '교육부 현장 교육 플랜 자문위원',
    '서울시교육청, 제주시교육지원청 위촉 강사',
    '전국 초, 중, 고등학교 대상 진로특강 강사'
  ];

  const tabs = ['비전', '목표', '기대 효과'];

  const tabContents = [
    {
      icon: 'ri-eye-line',
      title:
        '경험이 곧 경쟁력이 되는\n글로벌 진로교육',
      desc:
        'Branch는 단순한 체험을 넘어, 실질적인 경험이 곧 경쟁력이 되는 시대를 이끌어갑니다. 차별화된 국제교류와 진로교육을 통해 학생들이 직접 보고, 배우고, 경험하며 성장할 수 있도록 지원하고, 그 과정 속에서 진정한 성장의 가치를 발견하도록 돕습니다.'
    },
    {
      icon: 'ri-focus-3-line',
      title:
        '글로벌 시대를 갖추다\n미래 인재 양성',
      desc:
        '국제교육 프로그램을 통해 학생들이 다양한 문제를 경험하고, 직접 체험과 대학교 탐방을 통해 자신의 적성과 흥미를 발견하고, 구체적인 목표를 설정할 수 있도록 돕습니다.'
    },
    {
      icon: 'ri-rocket-line',
      title:
        '시야 확장과 진로 명확,\n그리고 소통 능력 향상',
      desc:
        '국제교육을 통해 세계를 바라보는 시각이 넓어지고, 직접 체험하면서 자신의 적성과 흥미를 발견합니다. 다양한 사람들과의 교육을 통해 소통 능력과 협업 능력이 자연스럽게 향상됩니다.'
    }
  ];

  const coreValues = [
    {
      icon: 'ri-eye-line',
      title: 'Experience-Based Learning',
      subtitle: '경험 중심 교육',
      desc: '직접 보고, 체험하며 배우는 경험 중심 교육'
    },
    {
      icon: 'ri-compass-3-line',
      title: 'Career Discovery',
      subtitle: '진로 탐색',
      desc: '다양한 직업 세계를 경험하며 자신의 진로와 가능성을 탐색'
    },
    {
      icon: 'ri-line-chart-line',
      title: 'Practical Competency',
      subtitle: '실무 역량',
      desc: '현장 중심 교육을 통한 실무 역량 강화'
    },
    {
      icon: 'ri-links-line',
      title: 'Educational Connection',
      subtitle: '교육 네트워크',
      desc: '학교, 기업, 전문가를 연결하는 교육 네트워크 구축'
    }
  ];

  const businessGoals = [
    {
      number: '01',
      title: '경험 기반 교육 확대',
      desc: '직접 보고 체험하는 교육 프로그램을 통해 경험 중심 학습 환경을 확대합니다.'
    },
    {
      number: '02',
      title: '진로 탐색 및 직업 이해 지원',
      desc: '다양한 직업 체험을 통해 참가자들이 자신의 진로 방향을 발견하도록 돕습니다.'
    },
    {
      number: '03',
      title: '실무 역량 및 자기계발 지원',
      desc: '성인과 기업 임직원이 새로운 역량을 개발하고 전문성을 강화할 수 있도록 지원합니다.'
    },
    {
      number: '04',
      title: '교육 협력 네트워크 구축',
      desc: '학교, 기업, 전문가와 협력하여 교육과 산업을 연결하는 프로그램을 운영합니다.'
    },
    {
      number: '05',
      title: '평생 교육 환경 조성',
      desc: '학생부터 성인까지 지속적으로 성장할 수 있는 교육 환경을 구축합니다.'
    }
  ];

  const expectedImpacts = [
    {
      number: '01',
      title: '진로 인식 확대',
      desc: '다양한 직업 경험을 통해 자신의 적성과 가능성을 발견할 수 있습니다.'
    },
    {
      number: '02',
      title: '경험 중심 학습 강화',
      desc: '현장 기반 교육을 통해 보다 깊이 있는 학습 경험을 제공합니다.'
    },
    {
      number: '03',
      title: '미래 역량 강화',
      desc: '창의성, 문제 해결 능력, 협업 능력 등 미래 사회 핵심 역량을 키울 수 있습니다.'
    },
    {
      number: '04',
      title: '교육과 산업의 연결 강화',
      desc: '학교와 기업을 연결하는 프로그램을 통해 현실적인 직업 이해를 돕습니다.'
    },
    {
      number: '05',
      title: '지속적인 성장 기반 마련',
      desc: '학생, 성인, 기업 임직원 모두가 지속적으로 성장할 수 있는 교육 기회를 제공합니다.'
    }
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Pretendard, sans-serif' }}>
      <Navbar />

      {/* Hero Section - Full width image background */}
      <section className="relative h-[45vh] md:h-[48vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/40405a80f429437b040e56bd9ce2d793.jpg"
            alt="회사소개 배경"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        </div>
        <div className="relative z-10 text-center px-6 w-full">
          <p className="text-sm md:text-base tracking-[0.3em] text-white/70 font-medium uppercase mb-4 md:mb-6">
            About Us
          </p>
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 md:mb-6 tracking-tight">
            회사소개
          </h1>
          <div className="w-16 md:w-20 h-0.5 bg-white/50 mx-auto mb-4 md:mb-6"></div>
          <p className="text-base md:text-xl text-white/80 leading-relaxed max-w-xl mx-auto">
            Branch는 국제교육과 진로교육을 통해
            <br className="hidden md:block" />
            미래 인재의 글로벌 역량을 키웁니다
          </p>
        </div>
      </section>

      {/* Vision Section - NEW */}
      <section className="py-20 md:py-32 px-6 bg-[#FDFBF7]">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <p className="text-sm tracking-[0.3em] text-[#8B7355] font-medium uppercase mb-3">
              Our Vision
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C2416] mb-6">
              회사 비전
            </h2>
            <div className="w-16 h-0.5 bg-[#8B7355] mx-auto"></div>
          </div>

          <div className="space-y-8 max-w-4xl mx-auto">
            <div className="bg-white p-8 md:p-12 rounded-2xl shadow-sm border border-[#E8DCC8]/50">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 flex items-center justify-center bg-[#8B7355] rounded-xl flex-shrink-0">
                  <i className="ri-eye-line text-2xl text-white"></i>
                </div>
                <div className="flex-1">
                  <p className="text-lg md:text-xl text-[#2C2416] leading-[1.9] font-medium">
                    Branch는 경험 중심 교육을 통해<br />
                    학생, 성인, 기업이 함께 성장할 수 있는<br />
                    <strong className="text-[#8B7355] font-bold">미래형 교육 플랫폼 구축</strong>을 목표로 합니다.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white p-8 md:p-12 rounded-2xl shadow-sm border border-[#E8DCC8]/50">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center bg-[#8B7355] rounded-xl flex-shrink-0">
                  <i className="ri-links-line text-2xl text-white"></i>
                </div>
                <div className="flex-1">
                  <p className="text-lg md:text-xl text-[#2C2416] leading-[1.9] font-medium">
                    학교와 산업 현장을 연결하는 다양한 교육 프로그램을 통해<br />
                    참가자들이 <strong className="text-[#8B7355] font-bold">실제 경험을 바탕으로 자신의 가능성을 발견</strong>하고<br />
                    지속적으로 성장할 수 있는 교육 환경을 만들어갑니다.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CEO Message */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">대표자 인사말</h2>
            <div className="w-20 h-1 bg-amber-700 mx-auto"></div>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="space-y-7 text-gray-700 leading-[2]">
              <p className="text-xl md:text-2xl font-semibold text-[#2C2416]">
                안녕하세요. Branch를 찾아주신 여러분께 진심으로 감사드립니다.
              </p>
              
              <p className="text-lg md:text-xl">
                오늘날 사회는 빠르게 변화하고 있으며,<br />
                단순한 지식 전달을 넘어 직접 경험하고 배우는 교육의 중요성이 점점 커지고 있습니다.<br />
                이제 교육은 교실 안에서만 이루어지는 것이 아니라<br />
                현장과 사회 속에서 다양한 경험을 통해 배우는 과정으로 확장되고 있습니다.
              </p>
              
              <p className="text-lg md:text-xl">
                Branch는 이러한 변화 속에서<br />
                학생의 진로 탐색부터 성인의 역량 개발, 기업 임직원의 성장까지<br />
                모든 세대를 위한 경험 기반 교육 프로그램을 운영하고 있습니다.
              </p>
              
              <p className="text-lg md:text-xl">
                직업체험, 진로교육, 국제교류, 대학탐방, 창업 및 역량 개발 프로그램 등<br />
                다양한 교육 프로그램을 통해 참가자들이 직접 경험하며<br />
                자신의 가능성을 발견할 수 있도록 돕고 있습니다.
              </p>
              
              <p className="text-lg md:text-xl">
                또한 학교, 기업, 전문가와의 협력을 통해<br />
                교육과 사회를 연결하는 실질적인 교육 환경을 만들고<br />
                미래 사회에 필요한 역량을 키울 수 있는 기회를 제공하고자 합니다.
              </p>
              
              <p className="text-xl md:text-2xl font-semibold text-[#2C2416]">
                앞으로도 Branch는 경험을 통해 배우고 성장하는 교육 환경을 만들며<br />
                더 많은 사람들이 자신의 가능성을 발견할 수 있도록 노력하겠습니다.
              </p>
              
              <p className="text-xl md:text-2xl font-semibold text-[#2C2416] pt-2">
                감사합니다.
              </p>
            </div>
            
            <div className="mt-10 pt-8 border-t border-amber-200 text-right">
              <p className="text-gray-900 font-bold text-xl">Branch 대표 올림.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Career & Activities */}
      <section id="ceo" className="py-20 md:py-32 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="mb-16">
            <div className="text-center mb-12">
              <p className="text-sm tracking-[0.3em] text-[#8B7355] font-medium uppercase mb-3">
                CEO Profile
              </p>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C2416] mb-4">
                대표자 약력
              </h2>
            </div>

            {/* Career & Activities */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              <div className="bg-[#FDFBF7] p-8 rounded-lg">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#8B7355] rounded-lg">
                    <i className="ri-award-line text-xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#2C2416]">주요 이력</h3>
                </div>
                <ul className="space-y-4">
                  {careerItems.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#8B7355] mt-2.5 flex-shrink-0"></span>
                      <span className="text-[15px] text-[#5A4A3A] leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-[#FDFBF7] p-8 rounded-lg">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#8B7355] rounded-lg">
                    <i className="ri-briefcase-line text-xl text-white"></i>
                  </div>
                  <h3 className="text-xl font-bold text-[#2C2416]">현재 활동</h3>
                </div>
                <ul className="space-y-4">
                  {activityItems.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#8B7355] mt-2.5 flex-shrink-0"></span>
                      <span className="text-[15px] text-[#5A4A3A] leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-6xl mx-auto px-6">
        <div className="h-px bg-[#E8DCC8]"></div>
      </div>

      {/* Core Values Section - NEW */}
      <section className="py-20 md:py-32 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm tracking-[0.3em] text-[#8B7355] font-medium uppercase mb-3">
              Core Value
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C2416] mb-4">
              핵심 가치
            </h2>
            <p className="text-base md:text-lg text-[#5A4A3A]">
              Branch가 추구하는 교육의 핵심 가치
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreValues.map((value, i) => (
              <div
                key={i}
                className="group rounded-2xl p-8 transition-all duration-500 cursor-pointer"
                style={{
                  backgroundColor: 
                    i === 0 ? '#F5EFE7' :
                    i === 1 ? '#E8DCC8' :
                    i === 2 ? '#D4C4A8' :
                    '#C0AD8E'
                }}
              >
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-white/90 transition-colors">
                    <i className={`${value.icon} text-3xl transition-colors`} style={{ color: i === 3 ? '#6d5a42' : '#8B7355' }}></i>
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-bold transition-colors" style={{ color: i === 3 ? '#1a1410' : '#2C2416' }}>
                      {value.title}
                    </h3>
                    <p className="text-sm font-semibold transition-colors" style={{ color: i === 3 ? '#4a3a2a' : '#8B7355' }}>
                      {value.subtitle}
                    </p>
                    <p className="text-sm leading-relaxed transition-colors" style={{ color: i === 3 ? '#3a2a1a' : '#5A4A3A' }}>
                      {value.desc}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DELETE Business Goals Section */}

      {/* DELETE Expected Impact Section */}

      {/* Vision / Mission / Values - Tab style */}
      <section className="py-20 md:py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm tracking-[0.3em] text-[#8B7355] font-medium uppercase mb-3">
              Our Vision
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C2416] mb-4">
              Branch의 목표
            </h2>
            <p className="text-base md:text-lg text-[#5A4A3A]">
              Branch가 추구하는 교육의 가치
            </p>
          </div>

          {/* Tabs */}
          <div className="flex items-center justify-center gap-2 mb-12 md:mb-16">
            {tabs.map((tab, i) => (
              <button
                key={i}
                onClick={() => setActiveTab(i)}
                className={`px-6 md:px-8 py-3 rounded-full text-sm md:text-base font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  activeTab === i
                    ? 'bg-[#2C2416] text-white'
                    : 'bg-[#F5EFE7] text-[#5A4A3A] hover:bg-[#E8DCC8]'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center min-h-[320px]">
            <div className="space-y-6">
              <div className="w-14 h-14 flex items-center justify-center bg-[#8B7355] rounded-2xl">
                <i className={`${tabContents[activeTab].icon} text-3xl text-white`}></i>
              </div>
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#2C2416] leading-snug whitespace-pre-line">
                {tabContents[activeTab].title}
              </h3>
              <p className="text-base md:text-lg text-[#5A4A3A] leading-[1.9]">
                {tabContents[activeTab].desc}
              </p>
            </div>
            <div className="relative">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=korean%20high%20school%20students%20walking%20through%20beautiful%20modern%20university%20campus%20with%20contemporary%20architecture%20in%20seoul%2C%20students%20wearing%20casual%20clothes%20looking%20at%20campus%20buildings%20with%20interest%20and%20curiosity%2C%20bright%20sunny%20day%20with%20green%20trees%20and%20open%20spaces%2C%20professional%20educational%20photography%2C%20inspiring%20academic%20atmosphere%20natural%20and%20authentic%20moment%20of%20campus%20exploration%20and%20future%20planning%20korean%20students%20only&width=800&height=1000&seq=program-university-korean-002&orientation=portrait"
                  alt="비전 이미지"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-[#F5EFE7] rounded-full -z-10"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-16 md:py-24 px-6 bg-[#2C2416]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
            <div className="text-center space-y-3" ref={stat1.ref}>
              <div className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">
                {stat1.count}
                <span className="text-3xl md:text-4xl text-[#8B7355]">+</span>
              </div>
              <p className="text-sm md:text-base text-white/60 font-medium">업계 경력 (년)</p>
            </div>
            <div className="text-center space-y-3" ref={stat2.ref}>
              <div className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">
                {stat2.count.toLocaleString()}
                <span className="text-3xl md:text-4xl text-[#8B7355]">명</span>
              </div>
              <p className="text-sm md:text-base text-white/60 font-medium">참여 학생 수</p>
            </div>
            <div className="text-center space-y-3" ref={stat3.ref}>
              <div className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">
                {stat3.count}
                <span className="text-3xl md:text-4xl text-[#8B7355]">+</span>
              </div>
              <p className="text-sm md:text-base text-white/60 font-medium">교육 기관</p>
            </div>
            <div className="text-center space-y-3" ref={stat4.ref}>
              <div className="text-4xl md:text-5xl lg:text-6xl font-bold text-white">
                {stat4.count}
                <span className="text-3xl md:text-4xl text-[#8B7355]">%</span>
              </div>
              <p className="text-sm md:text-base text-white/60 font-medium">만족도</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Cards */}
      <section className="py-20 md:py-32 px-6 bg-[#FDFBF7]">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-sm tracking-[0.3em] text-[#8B7355] font-medium uppercase mb-3">
              Our Mission
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C2416] mb-4">
              사업 목표
            </h2>
            <p className="text-base md:text-lg text-[#5A4A3A]">
              Branch가 추구하는 교육의 방향
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {missionItems.map((item, i) => (
              <div
                key={i}
                className="group bg-white p-8 rounded-lg hover:shadow-lg transition-all duration-300 border border-[#E8DCC8]/50 hover:border-[#8B7355]/30"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-[#F5EFE7] rounded-2xl mb-6 group-hover:bg-[#8B7355] transition-colors">
                  <i className={`${item.icon} text-2xl text-[#8B7355] group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-lg font-bold text-[#2C2416] mb-3">{item.title}</h3>
                <p className="text-sm text-[#5A4A3A] leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Meaning */}
      <section className="py-20 md:py-32 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="mb-12">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6">
              <i className="ri-plant-line text-5xl text-[#8B7355]"></i>
            </div>
            <p className="text-sm tracking-[0.3em] text-[#8B7355] font-medium uppercase mb-4">
              Brand Story
            </p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#2C2416] mb-8">
              Branch의 의미
            </h2>
          </div>
          <div className="space-y-6">
            <p className="text-lg md:text-xl text-[#5A4A3A] leading-[1.9] max-w-2xl mx-auto">
              Branch는 <strong className="text-[#2C2416]">&lsquo;나만의 길&rsquo;</strong>을 의미합니다.
              나만이 가치를 찾아 성장하고, 학생들이 다양한 경험을 통해
              자신의 길을 찾아 성장할 수 있도록 돕는 마음을 담았습니다.
            </p>
            <div className="inline-block bg-[#F5EFE7] px-8 py-4 rounded-full">
              <p className="text-base md:text-lg font-semibold text-[#8B7355]">
                경험이 곧 경쟁력이 되는 글로벌 진로교육
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
